import { Box, Heading, NumberInput, NumberInputField } from '@chakra-ui/react';
import { FC, useEffect, useState } from 'react';
import { AdElement } from '@adflow/types';

import { useRunTemplateBuilderOperation } from '../../operations/useRunTemplateBuilderOperation';

type PositionInputsProps = {
  selectedElement: AdElement;
};

const parseCoordinate = (value: string): number | null => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
};

const pixelCoordinate = (value: number): number => Math.round(value);

const PositionInputs: FC<PositionInputsProps> = ({ selectedElement }) => {
  const runTemplateOperation = useRunTemplateBuilderOperation();
  const [xDraft, setXDraft] = useState<string | null>(null);
  const [yDraft, setYDraft] = useState<string | null>(null);

  useEffect(() => {
    setXDraft(null);
  }, [selectedElement.id, selectedElement.position.x]);

  useEffect(() => {
    setYDraft(null);
  }, [selectedElement.id, selectedElement.position.y]);

  const handleXPositionChange = (xPosition: string): void => {
    const x = parseCoordinate(xPosition);
    if (x == null) {
      setXDraft(xPosition);
      return;
    }
    setXDraft(null);
    void runTemplateOperation('move_element', {
      elementId: selectedElement.id,
      x,
      y: selectedElement.position.y,
    });
  };

  const handleYPositionChange = (yPosition: string): void => {
    const y = parseCoordinate(yPosition);
    if (y == null) {
      setYDraft(yPosition);
      return;
    }
    setYDraft(null);
    void runTemplateOperation('move_element', {
      elementId: selectedElement.id,
      x: selectedElement.position.x,
      y,
    });
  };

  return (
    <>
      <Box display="flex" flexDirection="column">
        <Heading fontSize="md" fontWeight="semibold">
          X position
        </Heading>
        <NumberInput
          value={xDraft ?? pixelCoordinate(selectedElement.position.x)}
          size="sm"
          width={20}
          variant="outline"
          mt={2}
          precision={0}
          onChange={handleXPositionChange}
          onBlur={() => setXDraft(null)}
        >
          <NumberInputField data-test-id="x-position-input" />
        </NumberInput>
      </Box>
      <Box display="flex" flexDirection="column">
        <Heading fontSize="md" fontWeight="semibold">
          Y position
        </Heading>
        <NumberInput
          value={yDraft ?? pixelCoordinate(selectedElement.position.y)}
          size="sm"
          width={20}
          variant="outline"
          mt={2}
          precision={0}
          onChange={handleYPositionChange}
          onBlur={() => setYDraft(null)}
        >
          <NumberInputField data-test-id="y-position-input" />
        </NumberInput>
      </Box>
    </>
  );
};

PositionInputs.displayName = 'PositionInputs';

export default PositionInputs;
